import { useEffect, useMemo, useRef, useState } from "react";
import { useKeyListener } from "./useKeyListener/useKeyListener";
import { portalContainerId } from "../components/Portal/config";
/** Walks up the DOM tree until a mounted parent is found and returns it */
const getMountedParent = (node) => {
    if (node === null) {
        return null;
    }
    // is mounted?
    if (document.contains(node)) {
        return node;
    }
    return getMountedParent(node.parentNode);
};
/**
 * `useVisibilityController()` creates a `VisibilityController` which lets you
 * control the visibility state of component from inside and outside the
 * component with a convenient API.
 *
 * To control the visibility of your custom component just support a "controller
 * prop" (of type `VisibilityController`) and use its API inside your component.
 * By providing the controller as prop you get the advantage of controlling the
 * state also from outside the component.
 *
 * By default the controlled component will be hidden, if the user clicks
 * outside the component. You can disable the behaviour via the options
 * parameter. _Notice:_ To make this work, put the `ref` from the
 * `VisibilityController` into you controlled component (see example in
 * Storybook).
 */
export const useVisibilityController = (defaultVisibility = false, options = {}) => {
    const { onHide = () => { }, onShow = () => { }, onChanged = () => { }, } = options;
    /**
     * This ref is used to track if the mouse click event that would disabled the
     * visibility (hideOnClickOut) is not the same mouse event which enabled the
     * visibility.
     */
    const mouseDownAfterIsVisible = useRef(false);
    /** Setup the hideOnClickOut feature. */
    const clickOutRefs = useRef(null);
    const clickOutRefHandler = (ref) => {
        clickOutRefs.current = ref;
    };
    /**
     * Put it in state, because `hideOnClick` and `hideOnEsc` can be changed by
     * their respective update methods located in the Controller
     */
    const [hideOnClickOut, setHideOnClickOut] = useState(options.hideOnClickOut ?? false);
    const [hideOnEsc, setHideOnEsc] = useState(options.hideOnEsc ?? false);
    const [visible, setVisible] = useState(defaultVisibility);
    const [animationState, setAnimationState] = useState("initial");
    const hide = () => {
        setVisible(false);
    };
    const show = () => {
        setVisible(true);
    };
    const toggle = () => {
        setVisible((v) => !v);
    };
    // it's "pristine" until visibility has changed once
    const pristine = useRef(true);
    /** If visible state has changed, call the callbacks */
    useEffect(() => {
        if (visible) {
            setAnimationState("initial");
        }
        const isDirty = visible !== defaultVisibility;
        if (isDirty) {
            pristine.current = false;
        }
        if (pristine.current) {
            // skip calling the callbacks if "pristine"
            return;
        }
        if (visible) {
            onShow();
        }
        else {
            onHide();
        }
        onChanged(visible);
        return () => {
            pristine.current = true;
        };
    }, [visible]);
    const handleDocumentClick = (e) => {
        const currentRef = clickOutRefs.current;
        if (!currentRef || !e.target || !mouseDownAfterIsVisible.current) {
            return;
        }
        const clickedElement = getMountedParent(e.target);
        const portalContainer = currentRef.closest(`#${portalContainerId}`);
        if (!portalContainer) {
            return;
        }
        // close only the top level popover
        const closable = Array.from(portalContainer.children)
            .slice(0, -1)
            .every((portalElement) => !portalElement.contains(currentRef));
        if (clickedElement && !currentRef.contains(clickedElement) && closable) {
            hide();
        }
    };
    const handleDocumentMouseDown = () => {
        mouseDownAfterIsVisible.current = true;
    };
    useEffect(() => {
        if (!visible) {
            mouseDownAfterIsVisible.current = false;
            return;
        }
        if (!hideOnClickOut) {
            return;
        }
        /**
         * Keep this a click event (even if click includes mousedown), to be able to
         * stop propagation on other click events.
         */
        document.addEventListener("click", handleDocumentClick);
        document.addEventListener("mousedown", handleDocumentMouseDown);
        return () => {
            document.removeEventListener("click", handleDocumentClick);
            document.removeEventListener("mousedown", handleDocumentMouseDown);
        };
    }, [visible, hideOnClickOut]);
    const setVisibility = (visible) => {
        setVisible(visible);
    };
    // Setup hide on Escape
    const handleEscPressed = () => {
        if (hideOnEsc) {
            hide();
        }
    };
    useKeyListener("Escape", handleEscPressed, "keyup");
    return useMemo(() => ({
        hide,
        show,
        toggle,
        setVisibility,
        visible,
        hidden: !visible,
        setHideOnClickOut,
        setHideOnEsc,
        ref: clickOutRefHandler,
        isPristine: pristine.current,
        exitAnimationState: animationState,
        updateAnimationState: setAnimationState,
    }), [visible, animationState]);
};
//# sourceMappingURL=data:application/json;base64,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